// Moteur du do/undo local (per-user). Pur et sans réseau : il ne connaît ni le
// store ni le channel collab. Le câblage (subscriber de capture, actions
// undo/redo, UI) vit ailleurs ; ici on garde la mécanique testable en isolation.
//
// Une entrée de pile = { forward, inverse, focus } où forward/inverse sont des
// diffs produits par diffEngine (computeDiff) et focus désigne où naviguer après
// application (cf. focusFromDiff). La pile redo est alimentée par les undo.
//
// Portée : seules les modifications LOCALES de l'utilisateur sont capturées
// (le subscriber filtre remote/chargement). Conflit collaboratif = last-write-wins
// (un undo se ré-applique et gagne ; cf. docs/do-undo).

// Borne mémoire de chaque pile : au-delà, on jette les entrées les plus
// anciennes (les plus récentes sont les plus utiles à annuler).
export const MAX_STACK = 50;

// --- Garde d'application (miroir synchrone de remoteGuard) -------------------
// Vrai pendant qu'un undo/redo applique son diff au store. Le subscriber de
// capture le consulte pour ne PAS re-capturer ce qu'il vient d'appliquer (sinon
// boucle). structureSync, lui, ne le consulte pas : l'undo se diffuse et persiste
// aux co-éditeurs comme une édition normale.
let applyingUndo = false;

export const isApplyingUndo = () => applyingUndo;

export const withUndoApply = (fn) => {
  applyingUndo = true;
  try {
    fn();
  } finally {
    applyingUndo = false;
  }
};

// Mutations EPHEMERES : recalculs de layout/mesure (dimensions, clientTop/Left,
// shouldMeasure) déclenchés par le rendu/la sélection, PAS des éditions
// utilisateur. Elles ne doivent jamais entrer dans la pile undo (sinon
// sélectionner/rendre une box crée des entrées fantômes). Garde synchrone
// consultée par undoCapture, et par structureSync qui ne les envoie pas seules.
let applyingEphemeral = false;

export const isEphemeralMutation = () => applyingEphemeral;

export const withEphemeralMutation = (fn) => {
  applyingEphemeral = true;
  try {
    return fn();
  } finally {
    applyingEphemeral = false;
  }
};

// --- Miroir du panneau gauche (Variables) -----------------------------------
// overlayProps.left.shownStatus vit dans OverlayContext (state React), PAS dans
// le store → la capture (subscriber pur-store) ne le voit pas. OverlayContext
// pousse ici un booléen synchrone ; captureNow le lit AU MOMENT de la capture
// pour marquer focus.fromVariablesView. Ainsi l'undo d'une box éditée au
// formulaire rouvre la vue Variables (et non un scroll sur la page infinie),
// même si le panneau a changé entre l'édition et l'undo (nav cross-mode).
let leftViewIsVariables = false;

export const isVariablesView = () => leftViewIsVariables;

export const setVariablesView = (v) => {
  leftViewIsVariables = !!v;
};

// --- Pile bornée ------------------------------------------------------------
// Ajoute une entrée en tête (la plus récente) et borne la longueur. Pur :
// retourne une nouvelle pile, ne mute pas l'entrée passée.
export const pushCapped = (stack, entry, max = MAX_STACK) => {
  const next = [entry, ...stack];
  return next.length > max ? next.slice(0, max) : next;
};

// --- Aperçu PDF -------------------------------------------------------------
// Pile unique : toutes les éditions locales sont capturées, quel que soit le
// mode (page, formulaire, masque, configuration — la config et sa cascade dans
// les boxes sont annulables, les éditions faites en aperçu aussi). Seule
// restriction : déclencher undo/redo est interdit pendant l'aperçu PDF (on n'y
// édite pas le document). Gate des actions/UI uniquement, pas de la capture.
export const isPreviewMode = (state) =>
  !!(state.previewRenderState && (state.pagesPreview?.length ?? 0) > 0);

// --- Cible de navigation dérivée d'un diff ----------------------------------
// Après application d'un undo/redo, on amène l'utilisateur là où le changement a
// eu lieu (potentiellement dans un autre mode : masque, formulaire). La cible
// vient du diff lui-même (et non de la sélection : au flush d'une box texte,
// selectedBoxId est déjà null). Priorité box > variable > mask > container ;
// sinon pas de nav ciblée.
export const focusFromDiff = (diff) => {
  if (!diff) return null;
  const upserts = diff.upserts ?? {};
  const deletes = diff.deletes ?? {};
  const order = diff.order ?? {};

  const boxId =
    upserts.boxes?.[0]?.id ?? deletes.boxes?.[0] ?? null;
  if (boxId) return { type: 'box', id: boxId };

  const variableKey =
    Object.keys(upserts.variables ?? {})[0] ?? deletes.variables?.[0] ?? null;
  if (variableKey) return { type: 'variable', id: variableKey };

  const maskId =
    upserts.masks?.[0]?.id ?? deletes.masks?.[0] ?? null;
  if (maskId) return { type: 'mask', id: maskId };

  const containerId =
    upserts.containers?.[0]?.id ??
    deletes.containers?.[0] ??
    order.containers?.[0] ??
    null;
  if (containerId) return { type: 'container', id: containerId };

  return null;
};
